'use client';
import React from 'react';
import { ProductPrice as SharedProductPrice } from '@launchstore/shared-puck';
import type { ProductPriceProps, ProductData, ProductDataPrice } from '@launchstore/shared-puck';
import { useProduct } from '@/lib/hooks/useProduct';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { getProductPrice } from '@/lib/util/get-product-price';
import { findFirstPurchasableVariant } from '@/lib/util/variant-stock';

const toProductData = (p: any): ProductData => ({
  id: p.id, title: p.title, handle: p.handle, description: p.description,
  material: p.material, weight: p.weight, length: p.length, width: p.width, height: p.height,
  origin_country: p.origin_country, metadata: p.metadata,
  variants: (p.variants || []).map((v: any) => ({
    id: v.id, sku: v.sku, weight: v.weight, length: v.length, width: v.width, height: v.height,
  })),
});

const SharedProductPriceRender = (SharedProductPrice as any).render;

const ProductPriceImpl: React.FC<ProductPriceProps> = (props: any) => {
  const { product, selectedVariant } = useProduct() as any;
  const { context } = usePuckContext() as any;
  const resolved: ProductData | null = product ? toProductData(product) : null;

  const resolvePrice = (p: ProductData): ProductDataPrice | undefined => {
    if (!product) return undefined;
    let variantId = selectedVariant?.id;
    // Product cards with no variant picked show the first PURCHASABLE
    // variant's price — the variant the card's button is routing to.
    if (!variantId && context?.productCard) {
      variantId = findFirstPurchasableVariant(product.variants)?.id;
    }
    const { cheapestPrice, variantPrice } = getProductPrice({ product, variantId });
    const result = variantPrice || cheapestPrice;
    if (!result) return undefined;
    return {
      calculated_price: result.calculated_price,
      original_price: result.original_price,
      price_type: result.price_type,
      percentage_diff: result.percentage_diff,
    };
  };

  return <SharedProductPriceRender {...(props as any)} product={resolved} resolvePrice={resolvePrice} />;
};

export const ProductPrice: any = { ...SharedProductPrice, render: (props: any) => <ProductPriceImpl {...props} /> };
export default ProductPrice;
